import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';

// O(n) — usado no lugar de `.filter((x, i, list) => list.findIndex(...) === i)`,
// que é O(n²) e, com o quadro inteiro de itens, foi uma das causas dos
// travamentos de main-thread relatados (handlers de +800ms no console).
function dedupeById(list) {
  const seen = new Set();
  const result = [];
  for (const entry of list) {
    if (!entry?.id || seen.has(entry.id)) continue;
    seen.add(entry.id);
    result.push(entry);
  }
  return result;
}

export default function ModernItemEditor({ item, api, onClose, onItemUpdate, onDirtyChange, onOpenItem, members = [], allItems = [], subtasks = [], parentItem = null, customColumns = [], darkMode = false }) {
  const normalizeItemData = (sourceItem) => {
    if (!sourceItem) return {};
    const fallbackDescription = sourceItem.column_values?.description ?? sourceItem.description ?? sourceItem.notes ?? '';
    return {
      ...sourceItem,
      column_values: {
        ...(sourceItem.column_values || {}),
        description: fallbackDescription
      }
    };
  };

  const [itemData, setItemData] = useState(normalizeItemData(item));
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [boardItems, setBoardItems] = useState([]);
  const [savingDeps, setSavingDeps] = useState(false);
  const [selectedDeps, setSelectedDeps] = useState(item?.depends_on || []);
  const [timeInput, setTimeInput] = useState('');
  const [trackingActive, setTrackingActive] = useState(false);
  const [trackingStart, setTrackingStart] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [commentTab, setCommentTab] = useState('comments');
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [resolvedDependencies, setResolvedDependencies] = useState([]);
  const itemDataRef = useRef(normalizeItemData(item));
  const [savingItem, setSavingItem] = useState(false);
  const baselineRef = useRef('');

  const buildComparableSnapshot = (draftItem = itemData, deps = selectedDeps) => JSON.stringify({
    name: draftItem?.name || '',
    column_values: draftItem?.column_values || {},
    depends_on: [...(deps || [])].sort(),
    requires_approval: Boolean(draftItem?.requires_approval)
  });

  const customOptionsFor = (column) => {
    const options = column.settings?.options || column.options || [];
    const list = Array.isArray(options) ? options : String(options).split(/[\\,|]/).filter(Boolean);
    return list.map(option => {
      if (typeof option === 'object' && option !== null) {
        const value = option.value ?? option.id ?? option.label ?? option.name;
        return { value: String(value ?? ''), label: option.label || option.name || String(value ?? '') };
      }
      return { value: String(option), label: String(option) };
    }).filter(option => option.value);
  };

  const customInputValue = (column) => {
    const value = itemData.column_values?.[column.id];
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return value.value ?? value.id ?? value.name ?? value.label ?? '';
    return String(value);
  };

  const updateCustomDraft = (field, value) => {
    setItemData(current => {
      const updatedItem = {
        ...current,
        column_values: { ...(current.column_values || {}), [field]: value }
      };
      itemDataRef.current = updatedItem;
      return updatedItem;
    });
  };

  useEffect(() => {
    itemDataRef.current = itemData;
  }, [itemData]);

  useEffect(() => {
    if (item) {
      const normalizedItem = normalizeItemData(item);
      setItemData(normalizedItem);
      itemDataRef.current = normalizedItem;
      setSelectedDeps(item.depends_on || []);
      baselineRef.current = buildComparableSnapshot(normalizedItem, item.depends_on || []);
      setIsFocused(false);
      fetchItemDetails(item.id);
      fetchResolvedDependencies(item.id);
      fetchComments();
      fetchBoardItems();
      fetchFiles();
      fetchActivities();
    }
  }, [item]);

  const hasUnsavedChanges = useMemo(
    () => buildComparableSnapshot(itemData, selectedDeps) !== baselineRef.current,
    [itemData, selectedDeps]
  );

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  useEffect(() => {
    if (!item?.id) return;
    setBoardItems(dedupeById((allItems || [])
      .filter(candidate => candidate?.id && candidate.id !== item.id)));
  }, [allItems, item?.id]);

  const fetchBoardItems = async () => {
    try {
      if (!item?.board_id) return;
      const res = await api.get(`/projects/boards/${item.board_id}/items`);
      const fetchedItems = Array.isArray(res.data) ? res.data : [];
      const mergedItems = dedupeById([...fetchedItems, ...allItems]
        .filter(candidate => candidate?.id && candidate.id !== item.id));
      setBoardItems(mergedItems);
    } catch (e) {
      if (allItems.length > 0) {
        setBoardItems(allItems.filter(i => i.id !== item.id));
      }
    }
  };

  const fetchItemDetails = async (itemId) => {
    if (!itemId) return;
    setLoading(true);
    try {
      const res = await api.get(`/projects/items/${itemId}`);
      const normalizedItem = normalizeItemData(res.data);
      setItemData(normalizedItem);
      itemDataRef.current = normalizedItem;
      setSelectedDeps(res.data?.depends_on || []);
      baselineRef.current = buildComparableSnapshot(normalizedItem, res.data?.depends_on || []);
    } catch {
      const fallbackItem = normalizeItemData(item);
      setItemData(fallbackItem);
      itemDataRef.current = fallbackItem;
      setSelectedDeps(item?.depends_on || []);
      baselineRef.current = buildComparableSnapshot(fallbackItem, item?.depends_on || []);
    } finally {
      setLoading(false);
    }
  };

  const fetchResolvedDependencies = async (itemId) => {
    if (!itemId) return;
    try {
      const res = await api.get(`/projects/items/${itemId}/dependencies`);
      setResolvedDependencies(Array.isArray(res.data) ? res.data : []);
    } catch {
      setResolvedDependencies([]);
    }
  };

  const fetchActivities = async () => {
    if (!item?.id) return;
    setActivitiesLoading(true);
    try {
      const res = await api.get(`/projects/items/${item.id}/activity`);
      setActivities(res.data || []);
    } catch (e) {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleReaction = async (commentId, emoji) => {
    try {
      const res = await api.post(`/projects/comments/${commentId}/react?emoji=${encodeURIComponent(emoji)}`);
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, reactions: res.data.reactions } : c
      ));
    } catch (e) {
      toast.error('Erro ao reagir ao comentário');
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    const parts = [];
    let remaining = text;
    let key = 0;

    const patterns = [
      { regex: /\*\*(.+?)\*\*/s, render: (m) => <strong key={key++} className="font-semibold">{m[1]}</strong> },
      { regex: /_(.+?)_/s, render: (m) => <em key={key++} className="italic">{m[1]}</em> },
      { regex: /`(.+?)`/, render: (m) => <code key={key++} className="bg-gray-100 text-red-600 px-1 rounded text-xs font-mono">{m[1]}</code> },
    ];

    const lines = text.split('\n');
    return lines.map((line, lineIdx) => {
      let remaining = line;
      const lineElements = [];
      let lk = 0;

      while (remaining.length > 0) {
        let earliest = null;
        let earliestIndex = Infinity;

        for (const p of patterns) {
          const match = p.regex.exec(remaining);
          if (match && match.index < earliestIndex) {
            earliest = { match, pattern: p };
            earliestIndex = match.index;
          }
        }

        if (!earliest) {
          lineElements.push(remaining);
          break;
        }

        if (earliest.match.index > 0) {
          lineElements.push(remaining.slice(0, earliest.match.index));
        }
        lineElements.push(earliest.pattern.render(earliest.match));
        remaining = remaining.slice(earliest.match.index + earliest.match[0].length);
      }

      return (
        <span key={lineIdx}>
          {lineIdx > 0 && <br />}
          {lineElements}
        </span>
      );
    });
  };

  const fetchFiles = async () => {
    if (!item?.id) return;
    setFilesLoading(true);
    try {
      const res = await api.get(`/projects/items/${item.id}/files`);
      setFiles(res.data || []);
    } catch (e) {
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const uploadFiles = async (fileList) => {
    if (fileList.length === 0) return;
    const toastId = 'editor-file-upload';
    const multiple = fileList.length > 1;
    let uploaded = 0;
    let failed = 0;
    // O backend só aceita um arquivo por requisição, então envia em sequência
    // — assim dá pra mostrar o progresso e um arquivo com erro não derruba os demais.
    for (let i = 0; i < fileList.length; i++) {
      const fileObj = fileList[i];
      const formData = new FormData();
      formData.append('file', fileObj);
      toast.loading(
        multiple ? `Enviando ${i + 1}/${fileList.length}: ${fileObj.name}` : `Enviando ${fileObj.name}...`,
        { id: toastId }
      );
      try {
        const res = await api.post(`/projects/items/${item.id}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setFiles(prev => [...prev, res.data]);
        uploaded += 1;
      } catch (err) {
        failed += 1;
      }
    }
    if (failed === 0) {
      toast.success(multiple ? `${uploaded} arquivos enviados!` : `"${fileList[0].name}" enviado!`, { id: toastId });
    } else if (uploaded === 0) {
      toast.error(multiple ? 'Erro ao enviar os arquivos' : 'Erro ao enviar arquivo', { id: toastId });
    } else {
      toast.warning(`${uploaded} enviado(s), ${failed} falharam`, { id: toastId });
    }
  };

  const handleFileUpload = async (e) => {
    await uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // Arrastar-e-soltar: funciona tanto na área vazia quanto sobre a lista de
  // arquivos já anexados (antes só existia o texto "arraste aqui", sem
  // nenhum handler de fato ligado a isso).
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);
  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleFileDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer?.types?.includes('Files')) return;
    dragCounterRef.current += 1;
    setIsDraggingFile(true);
  };
  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingFile(false);
  };
  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    await uploadFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleFileDelete = async (fileId, fileName) => {
    if (!window.confirm(`Excluir "${fileName}"?`)) return;
    try {
      await api.delete(`/projects/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('Arquivo excluído');
    } catch (err) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  // Baixar/visualizar precisam passar pelo `api` (com o Bearer token) — o
  // endpoint exige autenticação e um <a href> puro nem manda o token nem
  // aponta para o backend correto, então nunca funcionava.
  const handleFileDownload = async (fileId, fileName) => {
    try {
      const res = await api.get(`/projects/files/${fileId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleFileView = async (fileId) => {
    try {
      const res = await api.get(`/projects/files/${fileId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error('Erro ao abrir arquivo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimetype) => {
    if (!mimetype) return '📄';
    if (mimetype.startsWith('image/')) return '🖼️';
    if (mimetype === 'application/pdf') return '📕';
    if (mimetype.includes('spreadsheet') || mimetype.includes('excel')) return '📊';
    if (mimetype.includes('word') || mimetype.includes('document')) return '📝';
    if (mimetype.startsWith('video/')) return '🎬';
    if (mimetype.startsWith('audio/')) return '🎵';
    if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('tar')) return '🗜️';
    return '📄';
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/projects/items/${item.id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      setComments([
        {
          id: '1',
          content: 'Atualização: Este item foi priorizado para esta semana.',
          author: { name: 'João Silva', email: 'joao@empresa.com' },
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '2',
          content: 'Ótimo! Vou começar a trabalhar nisso hoje.',
          author: { name: 'Maria Santos', email: 'maria@empresa.com' },
          created_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        }
      ]);
    }
  };

  const handleFieldUpdate = (field, value, { closeEditor = true } = {}) => {
    setItemData(current => {
      const updatedItem = {
        ...current,
        column_values: { ...(current.column_values || {}), [field]: value }
      };
      itemDataRef.current = updatedItem;
      return updatedItem;
    });
    if (closeEditor) setEditingField(null);
  };

  // Alterna um membro do quadro na lista de responsáveis do item.
  const toggleAssignee = (memberId) => {
    const current = itemData.column_values?.assigned_members || [];
    const updated = current.includes(memberId)
      ? current.filter(id => id !== memberId)
      : [...current, memberId];
    handleFieldUpdate('assigned_members', updated, { closeEditor: false });
  };

  const handleNameUpdate = (newName) => {
    if (!newName.trim()) return;
    setItemData(current => {
      const updatedItem = { ...current, name: newName.trim() };
      itemDataRef.current = updatedItem;
      return updatedItem;
    });
    setEditingField(null);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const response = await api.post(`/projects/items/${itemData.id}/comments`, {
        content: newComment,
        item_id: itemData.id
      });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      toast.success('Comentário adicionado!');
    } catch (error) {
      const newCommentObj = {
        id: Date.now().toString(),
        content: newComment,
        author: { name: 'Usuário Atual', email: 'usuario@empresa.com' },
        created_at: new Date().toISOString(),
      };
      setComments(prev => [...prev, newCommentObj]);
      setNewComment('');
      toast.success('Comentário adicionado!');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveItem = async ({ closeAfterSave = false } = {}) => {
    const currentItem = itemDataRef.current || itemData;
    if (!currentItem?.id) return;
    setSavingItem(true);
    setSavingDeps(true);
    try {
      await api.put(`/projects/items/${currentItem.id}`, {
        name: currentItem.name?.trim() || currentItem.name || '',
        column_values: currentItem.column_values || {},
        depends_on: selectedDeps,
        requires_approval: Boolean(currentItem.requires_approval)
      });
      const updatedItem = {
        ...currentItem,
        name: currentItem.name?.trim() || currentItem.name || '',
        depends_on: selectedDeps
      };
      setItemData(updatedItem);
      itemDataRef.current = updatedItem;
      setResolvedDependencies(currentDeps => {
        const dependencyMap = new Map([...boardItems, ...currentDeps].map(dep => [dep.id, dep]));
        return selectedDeps.map(depId => dependencyMap.get(depId)).filter(Boolean);
      });
      baselineRef.current = buildComparableSnapshot(updatedItem, selectedDeps);
      onItemUpdate?.(updatedItem);
      toast.success('Item atualizado!');
      if (closeAfterSave) onClose?.();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Erro ao salvar item');
    } finally {
      setSavingItem(false);
      setSavingDeps(false);
    }
  };

  const toggleDep = (depId) => {
    setSelectedDeps(prev =>
      prev.includes(depId) ? prev.filter(id => id !== depId) : [...prev, depId]
    );
  };

  const handleAddTimeLog = async () => {
    if (!timeInput.trim()) return;
    const parts = timeInput.split(':').map(Number);
    let minutes = 0;
    if (parts.length === 3) minutes = parts[0] * 60 + parts[1];
    else if (parts.length === 2) minutes = parts[0] * 60 + parts[1];
    else minutes = parseInt(timeInput) || 0;
    const existing = itemData.column_values?.time_tracked || 0;
    handleFieldUpdate('time_tracked', existing + minutes);
    setTimeInput('');
  };

  const formatMinutes = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'feito': 'bg-green-500',
      'em_progresso': 'bg-blue-500',
      'parado': 'bg-red-500',
      'pendente': 'bg-yellow-500',
      'working_on_it': 'bg-orange-500',
      'done': 'bg-green-500',
      'to_do': 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-400';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'alta': 'bg-red-100 text-red-800 border-red-200',
      'media': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'baixa': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length > 1
      ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const getUserColor = (email) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
      'bg-yellow-500', 'bg-indigo-500', 'bg-red-500', 'bg-teal-500'
    ];
    const hash = email?.split('').reduce((a, b) => a + b.charCodeAt(0), 0) || 0;
    return colors[hash % colors.length];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return 'agora';
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  // Estas derivações cruzam boardItems/subtasks (podem ser centenas de itens
  // do quadro inteiro) e antes rodavam de novo, sem memoização, A CADA
  // renderização do editor — inclusive ao digitar em qualquer campo ou ao
  // fechar um diálogo, o que gerava os travamentos de +800ms vistos no
  // console ("[Violation] 'click'/'message' handler took Xms"). Também
  // usavam `.filter(...).findIndex(...)`/`.some(...)` aninhados, ou seja
  // O(n²). Agora só recalcula quando os dados relevantes mudam de fato, e a
  // deduplicação usa um Map/Set em uma passada só (O(n)).
  const assignedMemberIds = itemData.column_values?.assigned_members;
  const itemDueDate = itemData.column_values?.due_date;
  const itemStatus = itemData.column_values?.status;
  const {
    assignedMembers, inheritedDeadline, inheritedAssignees, peopleFromItem,
    rollupPeople, subtaskDueDates, latestSubtaskDue, overdueSubtasks, taskOverdue,
    dependencyOptions, dependencyParents, dependencyChildrenByParent,
    orphanDependencyChildren, depItems,
  } = useMemo(() => {
    const assignedMembers = (assignedMemberIds || []).map(id =>
      members.find(m => m.id === id)
    ).filter(Boolean);
    const inheritedDeadline = parentItem?.column_values?.due_date || null;
    const inheritedAssignees = parentItem?.column_values?.persons || (parentItem?.column_values?.person ? [{ name: parentItem.column_values.person, id: parentItem.column_values.person_id || parentItem.column_values.person }] : []);
    const peopleFromItem = (source) => {
      if (!source) return [];
      const values = source.column_values || source;
      if (Array.isArray(values.persons)) return values.persons.map(person => typeof person === 'string' ? { id: person, name: members.find(member => member.id === person)?.name || person } : person).filter(Boolean);
      if (Array.isArray(values.assigned_members)) return values.assigned_members.map(id => ({ id, name: members.find(member => member.id === id)?.name || id }));
      if (values.person) return [{ id: values.person_id || values.person, name: values.person }];
      return [];
    };
    const rollupPeopleMap = new Map();
    for (const person of [...assignedMembers.map(member => ({ id: member.id, name: member.user_name || member.name })), ...subtasks.flatMap(peopleFromItem), ...inheritedAssignees]) {
      if (!person?.name) continue;
      const key = String(person.id || person.name);
      if (!rollupPeopleMap.has(key)) rollupPeopleMap.set(key, person);
    }
    const rollupPeople = [...rollupPeopleMap.values()];
    const subtaskDueDates = subtasks.map(subtask => subtask.column_values?.due_date).filter(Boolean).map(date => new Date(date));
    const latestSubtaskDue = subtaskDueDates.length ? new Date(Math.max(...subtaskDueDates.map(date => date.getTime()))) : null;
    const overdueSubtasks = subtasks.filter(subtask => subtask.column_values?.due_date && new Date(subtask.column_values.due_date) < new Date() && !['feito', 'done', 'concluido', 'concluído'].includes(String(subtask.column_values?.status || '').toLowerCase())).length;
    const taskOverdue = itemDueDate && new Date(itemDueDate) < new Date() && !['feito', 'done', 'concluido', 'concluído'].includes(String(itemStatus || '').toLowerCase());

    const dependencyOptionsMap = new Map();
    for (const candidate of [...boardItems, ...resolvedDependencies]) {
      if (!candidate?.id || candidate.id === itemData.id || dependencyOptionsMap.has(candidate.id)) continue;
      dependencyOptionsMap.set(candidate.id, candidate);
    }
    const dependencyOptions = [...dependencyOptionsMap.values()];
    const dependencyParents = dependencyOptions.filter(candidate => !candidate.parent_id);
    const dependencyChildrenByParent = dependencyOptions.reduce((accumulator, candidate) => {
      if (!candidate.parent_id) return accumulator;
      if (!accumulator[candidate.parent_id]) accumulator[candidate.parent_id] = [];
      accumulator[candidate.parent_id].push(candidate);
      return accumulator;
    }, {});
    const orphanDependencyChildren = dependencyOptions.filter(candidate => candidate.parent_id && !dependencyOptionsMap.has(candidate.parent_id));
    const depItems = dependencyOptions.filter(candidate => selectedDeps.includes(candidate.id));

    return {
      assignedMembers, inheritedDeadline, inheritedAssignees, peopleFromItem,
      rollupPeople, subtaskDueDates, latestSubtaskDue, overdueSubtasks, taskOverdue,
      dependencyOptions, dependencyParents, dependencyChildrenByParent,
      orphanDependencyChildren, depItems,
    };
  }, [assignedMemberIds, itemDueDate, itemStatus, itemData.id, members, parentItem, subtasks, boardItems, resolvedDependencies, selectedDeps]);
  // Primeira subtarefa ainda não concluída, na mesma ordem exibida na seção
  // "Subtarefas" — é o que aparece como "próxima tarefa" nos Alertas de gestão.
  const nextPendingSubtask = subtasks.find(
    subtask => !['feito', 'done', 'concluido', 'concluído'].includes(String(subtask.column_values?.status || '').toLowerCase())
  ) || null;
  const managementAlerts = [
    !rollupPeople.length && { tone: 'amber', label: 'Sem responsável definido', detail: 'Atribua uma pessoa para garantir a execução.' },
    taskOverdue && { tone: 'red', label: 'Prazo da tarefa vencido', detail: 'Replaneje a data ou atualize o status.' },
    overdueSubtasks > 0 && { tone: 'red', label: `${overdueSubtasks} subtarefa(s) atrasada(s)`, detail: 'Revise os responsáveis e remova os bloqueios.' },
    selectedDeps.length > 0 && depItems.length < selectedDeps.length && { tone: 'amber', label: 'Dependência não localizada', detail: 'Uma dependência precisa ser revisada.' },
    !itemData.column_values?.next_action && { tone: 'blue', label: 'Próxima ação não definida', detail: 'Registre o próximo passo para manter o fluxo.' },
    nextPendingSubtask && {
      tone: 'blue',
      label: `Próxima tarefa: ${nextPendingSubtask.name}`,
      detail: [
        nextPendingSubtask.column_values?.due_date ? `prazo ${new Date(nextPendingSubtask.column_values.due_date).toLocaleDateString('pt-BR')}` : null,
        peopleFromItem(nextPendingSubtask).map(person => person.name).filter(Boolean).join(', ') || 'sem responsável'
      ].filter(Boolean).join(' · ')
    },
  ].filter(Boolean);
  const depsChanged = JSON.stringify([...selectedDeps].sort()) !== JSON.stringify([...(itemData.depends_on || [])].sort());
  const subtaskDone = subtasks.filter(subtask => ['feito', 'done', 'concluido', 'concluído'].includes(String(subtask.column_values?.status || '').toLowerCase())).length;
  const renderCustomField = (column) => {
    const type = String(column.type || 'text').toLowerCase();
    const value = customInputValue(column);
    const options = customOptionsFor(column);

    if (['file', 'formula', 'time_tracking', 'rating', 'vote'].includes(type)) {
      const display = type === 'formula' ? (value || 'Calculado automaticamente') :
        type === 'file' ? (Array.isArray(itemData.column_values?.[column.id]) ? `${itemData.column_values[column.id].length} arquivo(s)` : 'Gerencie nos arquivos') :
        type === 'time_tracking' ? 'Disponível somente no histórico' : (value || 'Sem dados');
      return <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">{display}</div>;
    }

    if (type === 'checkbox' || type === 'check') {
      return <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700"><input type="checkbox" checked={Boolean(itemData.column_values?.[column.id])} onChange={event => updateCustomDraft(column.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />{itemData.column_values?.[column.id] ? 'Sim' : 'Não'}</label>;
    }

    if (['dropdown', 'lista_suspensa', 'select', 'status'].includes(type) && options.length) {
      return <select value={value} onChange={event => updateCustomDraft(column.id, event.target.value)} className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"><option value="">Selecionar…</option>{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>;
    }

    if (type === 'person') {
      return <select value={value} onChange={event => updateCustomDraft(column.id, event.target.value)} className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"><option value="">Sem responsável</option>{members.map(member => { const memberId = member.user_id || member.id || member.email; const memberName = member.user_name || member.name || member.user_email || member.email; return <option key={memberId} value={memberId}>{memberName}</option>; })}</select>;
    }

    const inputType = ['number', 'numbers', 'numero', 'progress', 'progresso'].includes(type) ? 'number' : ['date', 'data'].includes(type) ? 'date' : 'text';
    const fieldValue = inputType === 'date' ? value.slice(0, 10) : value;
    return <Input type={inputType} value={fieldValue} placeholder={`Preencher ${column.title}`} min={inputType === 'number' ? '0' : undefined} max={['progress', 'progresso'].includes(type) ? '100' : undefined} onChange={event => updateCustomDraft(column.id, event.target.value)} />;
  };

  return (
    <div className={`relative flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 pb-8 transition-all duration-200 sm:p-6 xl:pr-[460px] ${darkMode ? 'nexo-project-dark' : ''} ${isFocused ? 'fixed inset-0 z-[9999] max-h-screen bg-white p-8' : ''}`}>
      <div className="order-3 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <div className="md:col-span-2"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Localização</p><p className="mt-1 truncate text-sm font-semibold text-slate-800">{parentItem ? `Subtarefa de ${parentItem.name}` : 'Tarefa principal'}</p><p className="mt-1 text-xs text-slate-500">Nexo Project</p></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Prazo final</p><p className="mt-1 text-sm font-semibold text-slate-800">{itemData.column_values?.due_date ? new Date(itemData.column_values.due_date).toLocaleDateString('pt-BR') : inheritedDeadline ? `${new Date(inheritedDeadline).toLocaleDateString('pt-BR')} (herdado)` : 'Sem prazo'}</p></div>
        <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Responsáveis</p><div className="mt-1 flex items-center gap-1.5">{rollupPeople.length ? <>{rollupPeople.slice(0, 4).map(person => <span key={person.id || person.name} title={person.name} className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white ring-2 ring-white">{person.name.charAt(0).toUpperCase()}</span>)}<span className="text-xs font-semibold text-slate-700">{rollupPeople.length} pessoa(s)</span></> : <span className="text-sm font-semibold text-amber-600">Não atribuído</span>}</div></div>
      </div>
      <div className="order-4 grid gap-3 sm:grid-cols-3"><div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Cobertura de execução</p><p className="mt-1 text-sm font-semibold text-slate-800">{subtasks.length ? `${rollupPeople.length} responsáveis envolvidos` : 'Sem subtarefas para consolidar'}</p></div><div className="rounded-xl border border-slate-200 bg-white px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Maior prazo herdado</p><p className="mt-1 text-sm font-semibold text-slate-800">{latestSubtaskDue ? latestSubtaskDue.toLocaleDateString('pt-BR') : 'Sem prazo nas subtarefas'}</p></div><div className={`rounded-xl border px-4 py-3 ${overdueSubtasks ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Risco operacional</p><p className={`mt-1 text-sm font-semibold ${overdueSubtasks ? 'text-red-700' : 'text-emerald-700'}`}>{overdueSubtasks ? `${overdueSubtasks} subtarefa(s) atrasada(s)` : 'Nenhum atraso identificado'}</p></div></div>
      <div className="order-5 grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center justify-between"><div><p className="font-semibold text-slate-900">Próxima ação</p><p className="text-xs text-slate-500">O que precisa acontecer para esta tarefa avançar?</p></div><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Gestão</span></div><Input className="mt-3" placeholder="Ex.: Validar briefing com o cliente" value={itemData.column_values?.next_action || ''} onChange={e => updateCustomDraft('next_action', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} /></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center justify-between"><p className="font-semibold text-slate-900">Alertas de gestão</p><span className={`rounded-full px-2 py-1 text-[10px] font-bold ${managementAlerts.length ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{managementAlerts.length || 'OK'}</span></div><div className="mt-3 space-y-2">{managementAlerts.length ? managementAlerts.slice(0, 3).map((alert, index) => <div key={`${alert.label}-${index}`} className={`rounded-lg px-3 py-2 ${alert.tone === 'red' ? 'bg-red-50' : alert.tone === 'amber' ? 'bg-amber-50' : 'bg-blue-50'}`}><p className={`text-xs font-semibold ${alert.tone === 'red' ? 'text-red-700' : alert.tone === 'amber' ? 'text-amber-700' : 'text-blue-700'}`}>{alert.label}</p><p className="mt-0.5 text-[11px] text-slate-500">{alert.detail}</p></div>) : <p className="text-xs text-emerald-700">Nenhum alerta operacional no momento.</p>}</div></div>
      </div>
      {subtasks.length > 0 && <div className="order-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center justify-between"><div><p className="font-semibold text-slate-900">Subtarefas</p><p className="text-xs text-slate-500">Acompanhe o progresso herdado desta tarefa</p></div><span className="text-xs font-semibold text-blue-700">{subtaskDone}/{subtasks.length}</span></div><div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${(subtaskDone / subtasks.length) * 100}%` }} /></div><div className="mt-3 grid gap-2 sm:grid-cols-2">{subtasks.map(subtask => { const people = subtask.column_values?.persons || (subtask.column_values?.person ? [{ name: subtask.column_values.person }] : []); const done = ['feito', 'done', 'concluido', 'concluído'].includes(String(subtask.column_values?.status || '').toLowerCase()); return <button key={subtask.id} type="button" onClick={() => onOpenItem?.(subtask)} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-left transition-colors hover:border-blue-200 hover:bg-blue-50/60"><div className="min-w-0"><p className={`truncate text-xs font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{subtask.name}</p><p className="text-[11px] text-slate-400">{people.length ? people.map(person => person.name).join(', ') : 'Sem responsável'}{subtask.column_values?.due_date ? ` · ${new Date(subtask.column_values.due_date).toLocaleDateString('pt-BR')}` : ''}</p></div><div className="flex shrink-0 items-center gap-2"><span className="text-[11px] font-semibold text-blue-600">Abrir</span><span className={`text-xs ${done ? 'text-emerald-600' : 'text-slate-400'}`}>{done ? '✓' : '○'}</span></div></button>; })}</div></div>}
      {/* Header editável */}
      <div className="order-1 border-b border-gray-200 pb-4">
        <div className="flex items-start space-x-3">
          <div className="w-3 h-3 bg-blue-500 rounded-full mt-2"></div>
          <div className="flex-1 space-y-3">
            {/* 2.6 Focus mode toggle */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
              <button
                onClick={() => setIsFocused(f => !f)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                title={isFocused ? 'Sair do modo foco' : 'Modo foco (tela cheia)'}
              >
                {isFocused ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 9L4 4m0 0l5 0M4 4v5m11-5l5 5m0-5h-5m0 0v5M9 15l-5 5m0 0h5m-5 0v-5m11 5l5-5m0 5h-5m0 0v-5" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
            </div>
          
            <div>
              {editingField === 'name' ? (
                <div className="flex items-center space-x-2">
                  <Input
                    defaultValue={itemData.name}
                    onBlur={(e) => handleNameUpdate(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNameUpdate(e.target.value); }}
                    className="text-lg font-semibold"
                    autoFocus
                  />
                  <Button size="sm" onClick={() => setEditingField(null)} variant="outline">Cancelar</Button>
                </div>
              ) : (
                <h3
                  className="text-lg font-semibold text-gray-900 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  onClick={() => setEditingField('name')}
                >
                  {itemData.name}
                </h3>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Descrição</label>
              <Textarea
                className="mt-1 min-h-[88px] resize-y"
                placeholder="Descreva o objetivo, contexto ou critérios desta tarefa"
                value={itemData.column_values?.description || ''}
                onChange={(event) => updateCustomDraft('description', event.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Status</label>
                <div className="mt-1">
                  {editingField === 'status' ? (
                    <Select
                      value={itemData.column_values?.status || '__no_status__'}
                      onValueChange={(value) => handleFieldUpdate('status', value === '__no_status__' ? '' : value)}
                    >
                      <SelectTrigger className={`w-full ${getStatusColor(itemData.column_values?.status)}`}><SelectValue placeholder="Sem status" /></SelectTrigger>
                      <SelectContent className="z-[100] rounded-xl p-1.5 shadow-xl">
                        <SelectItem value="__no_status__" className="rounded-lg text-sm text-slate-500">Sem status</SelectItem>
                        {[...new Set(['to_do', 'working_on_it', 'em_progresso', 'feito', 'parado', 'pendente', itemData.column_values?.status].filter(Boolean))].map(status => <SelectItem key={status} value={status} className="mt-1 rounded-lg text-sm font-medium">{status === 'to_do' ? '📋 A fazer' : status === 'working_on_it' ? '🔥 Em andamento' : status === 'em_progresso' ? '⚡ Em progresso' : status === 'feito' ? '✅ Feito' : status === 'parado' ? '🚫 Parado' : status === 'pendente' ? '⏸️ Pendente' : String(status).replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white cursor-pointer hover:opacity-80 ${getStatusColor(itemData.column_values?.status)}`}
                      onClick={() => setEditingField('status')}
                    >
                      {itemData.column_values?.status || 'Sem status'}
                    </div>
                  )}
                </div>
              </div>

              {/* Prioridade */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Prioridade</label>
                <div className="mt-1">
                  {editingField === 'priority' ? (
                    <Select
                      value={itemData.column_values?.priority || '__no_priority__'}
                      onValueChange={(value) => handleFieldUpdate('priority', value === '__no_priority__' ? '' : value)}
                    >
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__no_priority__">➖ Sem prioridade</SelectItem>
                        <SelectItem value="baixa">🟢 Baixa</SelectItem>
                        <SelectItem value="media">🟡 Média</SelectItem>
                        <SelectItem value="alta">🔴 Alta</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded border text-sm font-medium cursor-pointer hover:bg-opacity-75 ${getPriorityColor(itemData.column_values?.priority)}`}
                      onClick={() => setEditingField('priority')}
                    >
                      {itemData.column_values?.priority === 'alta' ? '🔴 Alta' :
                       itemData.column_values?.priority === 'media' ? '🟡 Média' :
                       itemData.column_values?.priority === 'baixa' ? '🟢 Baixa' : '➖ Sem prioridade'}
                    </div>
                  )}
                </div>
              </div>

              {/* Data */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Data</label>
                <div className="mt-1">
                  {editingField === 'due_date' ? (
                    <Input
                      type="date"
                      value={itemData.column_values?.due_date?.split('T')[0] || ''}
                      onChange={(e) => handleFieldUpdate('due_date', e.target.value)}
                      onBlur={() => setEditingField(null)}
                    />
                  ) : (
                    <div
                      className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm cursor-pointer hover:bg-green-200 inline-block"
                      onClick={() => setEditingField('due_date')}
                    >
                      {itemData.column_values?.due_date ? (
                        new Date(itemData.column_values.due_date).toLocaleDateString('pt-BR')
                      ) : '📅 Adicionar data'}
                    </div>
                  )}
                </div>
              </div>

              {/* Responsáveis */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Responsáveis</label>
                <div className="mt-1">
                  <Dialog>
                    <DialogTrigger asChild>
                      <div className="flex -space-x-1 cursor-pointer">
                        {assignedMembers.slice(0, 3).map((member, index) => (
                          <div
                            key={member?.id || index}
                            className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs text-white font-medium hover:z-10 ${getUserColor(member?.user_email || member?.email)}`}
                            title={member?.user_name || member?.name}
                          >
                            {getUserInitials(member?.user_name || member?.name)}
                          </div>
                        ))}
                        {assignedMembers.length > 3 && (
                          <div className="w-8 h-8 bg-slate-300 rounded-full border-2 border-white flex items-center justify-center text-[10px] text-slate-700 font-medium">
                            +{assignedMembers.length - 3}
                          </div>
                        )}
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs text-gray-500 hover:bg-gray-300">
                          +
                        </div>
                      </div>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                          Atribuir Responsável
                        </DialogTitle>
                        <p className="text-sm font-semibold uppercase text-gray-800">
                          Tarefa: {itemData.name}
                        </p>
                      </DialogHeader>
                      <div className="max-h-80 space-y-2 overflow-y-auto">
                        {members.length === 0 ? (
                          <p className="text-sm text-gray-400">Nenhum membro neste quadro</p>
                        ) : (
                          members.map(member => {
                            const checked = (itemData.column_values?.assigned_members || []).includes(member.id);
                            const memberName = member.user_name || member.name || member.user_email || member.email;
                            const memberEmail = member.user_email || member.email;
                            return (
                              <button
                                type="button"
                                key={member.id}
                                onClick={() => toggleAssignee(member.id)}
                                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${checked ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                              >
                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-medium text-white ${getUserColor(memberEmail)}`}>
                                  {getUserInitials(memberName)}
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-gray-900">{memberName}</p>
                                  <p className="truncate text-xs text-gray-500">{memberEmail}</p>
                                </div>
                                {checked && <span className="ml-auto text-blue-600">✓</span>}
                              </button>
                            );
                          })
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Fechar</Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Estimativa de tempo */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Estimativa de tempo</label>
                <div className="mt-1">
                  {editingField === 'estimated_time' ? (
                    <Input
                      autoFocus
                      value={itemData.column_values?.estimated_time || ''}
                      placeholder="Ex.: 2h 30min"
                      onChange={(e) => handleFieldUpdate('estimated_time', e.target.value, { closeEditor: false })}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    />
                  ) : (
                    <div
                      className="inline-block cursor-pointer rounded bg-violet-50 px-3 py-1 text-sm text-violet-800 hover:bg-violet-100"
                      onClick={() => setEditingField('estimated_time')}
                    >
                      ⏱️ {itemData.column_values?.estimated_time || 'Adicionar estimativa'}
                    </div>
                  )}
                </div>
              </div>

              {/* Dificuldade */}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wide">Dificuldade</label>
                <div className="mt-1">
                  {editingField === 'difficulty' ? (
                    <Select
                      value={itemData.column_values?.difficulty || 'media'}
                      onValueChange={(value) => handleFieldUpdate('difficulty', value)}
                    >
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="facil">🟢 Fácil</SelectItem>
                        <SelectItem value="media">🟡 Média</SelectItem>
                        <SelectItem value="dificil">🔴 Difícil</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div
                      className={`inline-block cursor-pointer rounded px-3 py-1 text-sm hover:opacity-80 ${itemData.column_values?.difficulty === 'dificil' ? 'bg-red-100 text-red-800' : itemData.column_values?.difficulty === 'facil' ? 'bg-green-100 text-green-800' : itemData.column_values?.difficulty === 'media' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}
                      onClick={() => setEditingField('difficulty')}
                    >
                      {itemData.column_values?.difficulty === 'dificil' ? '🔴 Difícil' : itemData.column_values?.difficulty === 'facil' ? '🟢 Fácil' : itemData.column_values?.difficulty === 'media' ? '🟡 Média' : '⚪ Não definida'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {customColumns.length > 0 && <section className="order-7 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"><div><h4 className="font-semibold text-slate-900">Campos personalizados</h4><p className="text-xs text-slate-500">Estes campos pertencem ao quadro e ficam disponíveis em cada tarefa.</p></div><span className="w-fit rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">{customColumns.length} ativo(s)</span></div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">{customColumns.map(column => <div key={column.id} className="space-y-1.5"><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{column.title}</label>{renderCustomField(column)}</div>)}</div>
      </section>}

      {/* ── Seção de Dependências ── */}
      <div className="order-8 space-y-3 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center space-x-2">
            <span>🔗</span>
            <span>Dependências</span>
            {selectedDeps.length > 0 && (
              <Badge variant="secondary">{selectedDeps.length}</Badge>
            )}
          </h4>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 italic">Carregando dependências...</p>
        ) : dependencyOptions.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Sem outros itens no quadro</p>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {dependencyParents.map(parentDependency => {
              const parentSelected = selectedDeps.includes(parentDependency.id);
              const parentDone = ['feito', 'done'].includes(parentDependency.column_values?.status);
              const childDependencies = dependencyChildrenByParent[parentDependency.id] || [];
              return (
                <div key={parentDependency.id} className="space-y-1">
                  <label
                    className={`flex items-center space-x-3 rounded p-2 cursor-pointer transition-colors ${
                      parentSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={parentSelected}
                      onChange={() => toggleDep(parentDependency.id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="flex-1 truncate text-sm font-medium text-gray-800">{parentDependency.name}</span>
                    {parentDone ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Concluído</span>
                    ) : (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">⏳ Pendente</span>
                    )}
                  </label>
                  {childDependencies.length > 0 && (
                    <div className="ml-6 space-y-1 border-l border-slate-200 pl-3">
                      {childDependencies.map(childDependency => {
                        const childSelected = selectedDeps.includes(childDependency.id);
                        const childDone = ['feito', 'done'].includes(childDependency.column_values?.status);
                        return (
                          <label
                            key={childDependency.id}
                            className={`flex items-center space-x-3 rounded p-2 cursor-pointer transition-colors ${
                              childSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={childSelected}
                              onChange={() => toggleDep(childDependency.id)}
                              className="rounded border-gray-300 text-blue-600"
                            />
                            <span className="flex-1 truncate text-sm text-gray-700">↳ {childDependency.name}</span>
                            {childDone ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Concluído</span>
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">⏳ Pendente</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {orphanDependencyChildren.map(childDependency => {
              const childSelected = selectedDeps.includes(childDependency.id);
              const childDone = ['feito', 'done'].includes(childDependency.column_values?.status);
              return (
                <label
                  key={childDependency.id}
                  className={`flex items-center space-x-3 rounded p-2 cursor-pointer transition-colors ${
                    childSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={childSelected}
                    onChange={() => toggleDep(childDependency.id)}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="flex-1 truncate text-sm text-gray-700">↳ {childDependency.name}</span>
                  {childDone ? (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✓ Concluído</span>
                  ) : (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">⏳ Pendente</span>
                  )}
                </label>
              );
            })}
          </div>
        )}

        {selectedDeps.length > 0 && !depsChanged && (
          <div className="text-xs text-gray-500">
            Este item depende de: {depItems.map(d => d.name).join(', ')}
          </div>
        )}
      </div>

      {/* ── Seção de Arquivos Anexados ── */}
      <div
        className="order-2 relative space-y-3"
        onDragEnter={handleFileDragEnter}
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
      >
        {isDraggingFile && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/90">
            <p className="text-sm font-medium text-blue-600">📎 Solte para anexar</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center space-x-2">
            <span>📎 Arquivos</span>
            <Badge variant="secondary">{files.length}</Badge>
          </h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            + Anexar
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </div>

        {filesLoading ? (
          <p className="text-sm text-gray-400 italic">Carregando arquivos...</p>
        ) : files.length === 0 ? (
          <div
            className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-blue-300 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <p className="text-sm text-gray-400">Clique ou arraste arquivos aqui</p>
            <p className="text-xs text-gray-300 mt-1">Máximo 25 MB por arquivo</p>
          </div>
        ) : (
          <div className="space-y-2">
            {files.map((f) => (
              <div
                key={f.id}
                className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-2 min-w-0">
                  <span className="text-xl flex-shrink-0">{getFileIcon(f.mimetype)}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{f.original_name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(f.size)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <button
                    onClick={() => handleFileView(f.id)}
                    className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100"
                  >
                    👁 Ver
                  </button>
                  <button
                    onClick={() => handleFileDownload(f.id, f.original_name)}
                    className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                  >
                    ⬇ Baixar
                  </button>
                  <button
                    onClick={() => handleFileDelete(f.id, f.original_name)}
                    className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Discussão + Histórico (abas) ── */}
      <div className="order-10 space-y-3 xl:absolute xl:bottom-0 xl:right-0 xl:top-0 xl:max-h-none xl:w-[430px] xl:overflow-y-auto xl:border-l xl:border-slate-200 xl:bg-white xl:p-6 xl:shadow-sm">
        {/* Tab bar */}
        <div className="flex items-center border-b border-gray-200">
          <button
            onClick={() => setCommentTab('comments')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${commentTab === 'comments' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            💬 Discussão <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{comments.length}</span>
          </button>
          <button
            onClick={() => { setCommentTab('history'); if (activities.length === 0) fetchActivities(); }}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${commentTab === 'history' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            📋 Histórico
          </button>
        </div>

        {/* Comments tab */}
        {commentTab === 'comments' && (
          <div className="space-y-3">
            <div className="space-y-3 max-h-56 overflow-y-auto">
              {comments.length === 0 && (
                <p className="text-sm text-gray-400 italic text-center py-4">Nenhum comentário ainda</p>
              )}
              {comments.map((comment) => (
                <div key={comment.id} className="flex space-x-3">
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium ${getUserColor(comment.author?.email)}`}>
                    {getUserInitials(comment.author?.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm text-gray-900">{comment.author?.name}</span>
                        <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                      </div>
                      {/* 2.2 Rich text / Markdown rendering */}
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {renderMarkdown(comment.content)}
                      </div>
                    </div>
                    {/* 2.3 Emoji Reactions */}
                    <div className="flex items-center flex-wrap gap-1 mt-1.5">
                      {Object.entries(comment.reactions || {}).map(([emoji, userIds]) => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(comment.id, emoji)}
                          className="flex items-center space-x-1 bg-gray-100 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-full px-2 py-0.5 text-xs transition-colors"
                        >
                          <span>{emoji}</span>
                          <span className="text-gray-600 font-medium">{userIds.length}</span>
                        </button>
                      ))}
                      {/* Quick emoji picker */}
                      {['👍','❤️','😄','🎉','👀','🚀'].map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(comment.id, emoji)}
                          className="text-sm opacity-0 hover:opacity-100 group-hover:opacity-100 bg-gray-50 hover:bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center transition-opacity"
                          title={`Reagir com ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-3 border-t pt-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-medium">U</div>
              <div className="flex-1 space-y-2">
                {/* 2.2 Markdown toolbar */}
                <div className="flex items-center space-x-1 border border-gray-200 rounded-t-md px-2 py-1 bg-gray-50">
                  {[
                    { label: 'B', action: (t) => `**${t || 'negrito'}**`, title: 'Negrito (**texto**)' },
                    { label: 'I', action: (t) => `_${t || 'itálico'}_`, title: 'Itálico (_texto_)', className: 'italic' },
                    { label: '<>', action: (t) => `\`${t || 'código'}\``, title: 'Código (`texto`)', className: 'font-mono' },
                  ].map(({ label, action, title, className }) => (
                    <button
                      key={label}
                      title={title}
                      className={`px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded ${className || ''}`}
                      onClick={() => {
                        const sel = newComment.slice(0, 99999);
                        setNewComment(prev => prev + action(''));
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  <span className="text-gray-300 text-xs ml-1">md</span>
                </div>
                <Textarea
                  placeholder="Adicione um comentário... (suporta **negrito**, _itálico_, `código`)"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAddComment(); }}
                  rows={2}
                  className="resize-none rounded-t-none border-t-0"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400">Ctrl+Enter para enviar</div>
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {submitting ? '...' : 'Comentar'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History tab — 2.4 */}
        {commentTab === 'history' && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {activitiesLoading ? (
              <p className="text-sm text-gray-400 italic text-center py-4">Carregando histórico...</p>
            ) : activities.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400">Nenhuma alteração registrada</p>
                <p className="text-xs text-gray-300 mt-1">As próximas mudanças aparecerão aqui</p>
              </div>
            ) : (
              activities.map((act) => (
                <div key={act.id} className="flex items-start space-x-2 py-2 border-b border-gray-50 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                    {act.user_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700">
                      <span className="font-medium">{act.user_name}</span>
                      {act.action === 'updated' && act.field && (
                        <> alterou <span className="font-medium text-gray-600">{act.field}</span>
                          {act.old_value && <> de <span className="line-through text-gray-400">"{act.old_value}"</span></>}
                          {act.new_value && <> para <span className="font-medium">"{act.new_value}"</span></>}
                        </>
                      )}
                      {act.action === 'created' && <> criou este item</>}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(act.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* 3.6 Approval Flow */}
      {itemData.requires_approval && (
        <div className={`order-11 rounded-lg border p-4 space-y-3 ${
          itemData.approval_status === 'approved' ? 'bg-green-50 border-green-200' :
          itemData.approval_status === 'rejected' ? 'bg-red-50 border-red-200' :
          itemData.approval_status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-800 flex items-center space-x-2">
              <span>✅ Fluxo de Aprovação</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                itemData.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                itemData.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                itemData.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {itemData.approval_status === 'approved' ? '✓ Aprovado' :
                 itemData.approval_status === 'rejected' ? '✗ Recusado' :
                 itemData.approval_status === 'pending' ? '⏳ Pendente' : 'Não enviado'}
              </span>
            </h4>
            {itemData.approval_status !== 'pending' && (
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const res = await api.post(`/projects/items/${itemData.id}/request-approval`);
                  setItemData(res.data);
                  if (onItemUpdate) onItemUpdate(res.data);
                  toast.success('Aprovação solicitada!');
                } catch (e) { toast.error('Erro ao solicitar aprovação'); }
              }}>
                Solicitar Aprovação
              </Button>
            )}
          </div>
          {itemData.approval_status === 'pending' && (
            <div className="flex items-center space-x-2">
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                const note = window.prompt('Nota de aprovação (opcional):') ?? '';
                try {
                  const res = await api.put(`/projects/items/${itemData.id}/approval`, { action: 'approve', note });
                  setItemData(res.data);
                  if (onItemUpdate) onItemUpdate(res.data);
                  toast.success('Item aprovado!');
                } catch (e) { toast.error('Erro ao aprovar — verifique suas permissões'); }
              }}>
                ✓ Aprovar
              </Button>
              <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={async () => {
                const note = window.prompt('Motivo da recusa:') ?? '';
                try {
                  const res = await api.put(`/projects/items/${itemData.id}/approval`, { action: 'reject', note });
                  setItemData(res.data);
                  if (onItemUpdate) onItemUpdate(res.data);
                  toast.success('Item recusado');
                } catch (e) { toast.error('Erro — verifique suas permissões'); }
              }}>
                ✗ Recusar
              </Button>
            </div>
          )}
          {itemData.approver_name && (
            <p className="text-xs text-gray-500">
              Por <span className="font-medium">{itemData.approver_name}</span>
              {itemData.approved_at && ` · ${new Date(itemData.approved_at).toLocaleString('pt-BR')}`}
              {itemData.approval_note && ` · "${itemData.approval_note}"`}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className={`order-12 mt-2 flex flex-col gap-3 rounded-2xl border px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5 ${darkMode ? 'border-slate-700 bg-slate-950' : 'border-slate-200 bg-white'}`}>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setItemData(current => ({ ...current, requires_approval: !current.requires_approval, approval_status: !current.requires_approval ? 'none' : current.approval_status }))}
            className={itemData.requires_approval ? 'border-green-300 text-green-600' : 'text-gray-500'}
          >
            {itemData.requires_approval ? '✅ Aprovação ativa' : '🔒 Ativar aprovação'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-500 border-red-200 hover:bg-red-50"
            onClick={async () => {
              if (!window.confirm('Excluir este item permanentemente?')) return;
              try {
                await api.delete(`/projects/items/${itemData.id}`);
                toast.success('Item excluído!');
                onItemUpdate?.();
                onClose?.();
              } catch { toast.error('Erro ao excluir item'); }
            }}
          >
            🗑️ Excluir
          </Button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Fechar</Button>
          <Button onClick={() => handleSaveItem()} disabled={savingItem} className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto">{savingItem ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </div>
    </div>
  );
}
